import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon, Download, FileSpreadsheet, Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { toast } from 'sonner';
import { invoiceTypeLabels } from '~/lib/invoice-type-config';
import { paymentMethodConfig, defaultPaymentMethod } from '~/lib/payment-method-config';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type InvoiceRow = {
    id: string;
    invoiceNumber: string;
    invoiceDate: string;
    type: string;
    totalGross: number;
    totalNet: number;
    vatAmount: number;
    hasPdf: boolean;
    billingDraftId: string;
    billingDraftNumber: string;
    customer: {
        id: number;
        firstName: string;
        lastName: string;
        email: string | null;
    };
    store: {
        id: number;
        name: string;
    };
};

type InvoiceExportRow = {
    invoiceNumber: string;
    invoiceDate: string;
    treatmentDate: string;
    type: string;
    storeName: string;
    totalGross: number;
    totalNet: number;
    customerName: string;
    customerEmail: string;
    billingDraftNumber: string;
    lineItemCount: number;
    paymentNumber: string;
    paymentMethod: string;
    comment: string;
};

const CSV_HEADERS = [
    'Rechnungsnummer',
    'Rechnungsdatum',
    'Leistungsdatum',
    'Rechnungstyp',
    'Standort',
    'Bruttobetrag',
    'Nettobetrag',
    'Kundenname',
    'Kunden_E-Mail',
    'Auftragsnummer',
    'Anzahl_Pos',
    'Zahlungsnummer',
    'Zahlungsmethode',
    'Kommentar',
];

const escapeCsvField = (value: string): string => {
    if (value.includes(';') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
};

const formatAmountForCsv = (amount: number): string =>
    amount.toFixed(2).replace('.', ',');

const generateCsv = (rows: InvoiceExportRow[]): string => {
    const header = CSV_HEADERS.map(escapeCsvField).join(';');
    const dataRows = rows.map(row => {
        const typeLabel = invoiceTypeLabels[row.type]?.label || row.type;
        const dateFormatted = format(new Date(row.invoiceDate), 'dd.MM.yyyy', { locale: de });
        const treatmentDateFormatted = format(new Date(row.treatmentDate), 'dd.MM.yyyy', { locale: de });
        const paymentMethodLabel = row.paymentMethod
            ? (paymentMethodConfig[row.paymentMethod] || defaultPaymentMethod).label
            : '';
        return [
            row.invoiceNumber,
            dateFormatted,
            treatmentDateFormatted,
            typeLabel,
            row.storeName,
            formatAmountForCsv(row.totalGross),
            formatAmountForCsv(row.totalNet),
            row.customerName,
            row.customerEmail,
            row.billingDraftNumber,
            String(row.lineItemCount),
            row.paymentNumber,
            paymentMethodLabel,
            row.comment,
        ].map(escapeCsvField).join(';');
    });

    return '\uFEFF' + [header, ...dataRows].join('\r\n');
};

const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const Invoices = () => {
    const navigate = useNavigate();
    const utils = trpc.useUtils();
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>(isAdmin ? 'all' : '');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [selectedBillingDraftType, setSelectedBillingDraftType] = useState<string>('all');
    const [page, setPage] = useState(0);
    const pageSize = 20;
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFrom, setExportFrom] = useState<Date | undefined>(undefined);
    const [exportTo, setExportTo] = useState<Date | undefined>(undefined);
    const [exportPopoverOpen, setExportPopoverOpen] = useState(false);

    const { data: storesData } = trpc.stores.getAll.useQuery({ filters: {} });
    const stores = useMemo(() => {
        const data = storesData?.data || [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }, [storesData?.data]);

    useEffect(() => {
        if (isAdmin && selectedStoreId === '') {
            setSelectedStoreId('all');
        } else if (!isAdmin && stores.length > 0 && !selectedStoreId) {
            setSelectedStoreId(String(stores[0].id));
        }
    }, [stores, isAdmin, selectedStoreId]);

    // Fetch invoices
    const { data: invoicesData, isLoading } = trpc.invoices.getAll.useQuery({
        page,
        pageSize,
        filters: {
            searchTerm: searchTerm || undefined,
            storeId: selectedStoreId !== 'all' ? parseInt(selectedStoreId) : undefined,
            type: selectedType !== 'all' ? selectedType as any : undefined,
            billingDraftType: selectedBillingDraftType !== 'all' ? selectedBillingDraftType as any : undefined,
        },
    }, {
        enabled: selectedStoreId !== '',
    });

    const downloadMutation = trpc.invoices.getDownloadUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
            setDownloadingId(null);
        },
        onError: (error) => {
            toast.error(`Fehler beim Download: ${error.message}`);
            setDownloadingId(null);
        },
    });

    const handleDownload = (invoiceId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setDownloadingId(invoiceId);
        downloadMutation.mutate({ id: invoiceId });
    };

    const handleExport = async () => {
        if (!exportFrom || !exportTo) {
            toast.error('Bitte wähle ein Start- und Enddatum.');
            return;
        }
        setIsExporting(true);
        try {
            const dateFrom = format(exportFrom, 'yyyy-MM-dd');
            const dateTo = format(exportTo, 'yyyy-MM-dd');
            const data = await utils.invoices.exportForCsv.fetch({ dateFrom, dateTo });
            if (data.length === 0) {
                toast.info('Keine Rechnungen im gewählten Zeitraum gefunden.');
                return;
            }
            const csv = generateCsv(data as InvoiceExportRow[]);
            const filename = `rechnungen_${dateFrom}_bis_${dateTo}.csv`;
            downloadCsv(csv, filename);
            toast.success(`${data.length} Rechnungen exportiert.`);
            setExportPopoverOpen(false);
        } catch (error) {
            toast.error('Fehler beim Export: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
        } finally {
            setIsExporting(false);
        }
    };

    const columns: ColumnDef<InvoiceRow>[] = [
        {
            accessorKey: 'invoiceNumber',
            header: 'Rechnungsnr.',
            cell: ({ row }) => (
                <div
                    className='font-mono text-sm cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/invoice/${row.original.id}`);
                    }}
                >
                    {row.original.invoiceNumber}
                </div>
            ),
        },
        {
            id: 'billingDraft',
            header: 'Entwurfsnr.',
            cell: ({ row }) => (
                <div
                    className='font-mono text-sm cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/billing-draft/${row.original.billingDraftId}`);
                    }}
                >
                    {row.original.billingDraftNumber}
                </div>
            ),
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => {
                const customer = row.original.customer;
                return (
                    <div>
                        <div
                            className='font-medium cursor-pointer hover:underline text-primary'
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/${customer.id}`);
                            }}
                        >
                            {customer.firstName} {customer.lastName}
                        </div>
                        {customer.email && (
                            <div className='text-sm text-muted-foreground'>{customer.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => row.original.store.name,
        },
        {
            accessorKey: 'invoiceDate',
            header: 'Datum',
            cell: ({ row }) => {
                const date = new Date(row.original.invoiceDate);
                return format(date, 'dd.MM.yyyy', { locale: de });
            },
        },
        {
            accessorKey: 'type',
            header: 'Typ',
            cell: ({ row }) => {
                const typeInfo = invoiceTypeLabels[row.original.type];
                if (!typeInfo) {
                    return <Badge variant='outline'>{row.original.type}</Badge>;
                }
                return (
                    <Badge variant={typeInfo.variant}>
                        {typeInfo.label}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'totalGross',
            header: 'Betrag',
            cell: ({ row }) => {
                const amount = Number(row.original.totalGross);
                const isNegative = amount < 0;
                return (
                    <div className={`font-medium ${isNegative ? 'text-red-600' : ''}`}>
                        {new Intl.NumberFormat('de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                        }).format(amount)}
                    </div>
                );
            },
        },
        {
            id: 'download',
            header: '',
            cell: ({ row }) => (
                <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={(e) => handleDownload(row.original.id, e)}
                    disabled={downloadingId === row.original.id}
                >
                    {downloadingId === row.original.id ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                        <Download className='h-4 w-4' />
                    )}
                </Button>
            ),
        },
    ];

    const table = useReactTable({
        data: (invoicesData?.data as InvoiceRow[]) || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        pageCount: invoicesData?.pageCount || 0,
    });

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex justify-between items-center mb-6'>
                    <h1 className='text-2xl font-bold'>Rechnungen</h1>
                    <Popover open={exportPopoverOpen} onOpenChange={setExportPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button variant='outline'>
                                <FileSpreadsheet className='h-4 w-4 mr-2' />
                                Export
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className='w-auto p-4' align='end'>
                            <div className='space-y-4'>
                                <div className='text-sm font-medium'>Zeitraum wählen</div>
                                <div className='flex gap-4'>
                                    <div className='space-y-2'>
                                        <label className='text-xs text-muted-foreground'>Von</label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant='outline' className='w-[160px] justify-start text-left font-normal'>
                                                    <CalendarIcon className='h-4 w-4 mr-2' />
                                                    {exportFrom
                                                        ? format(exportFrom, 'dd.MM.yyyy', { locale: de })
                                                        : 'Startdatum'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className='w-auto p-0' align='start'>
                                                <Calendar
                                                    mode='single'
                                                    selected={exportFrom}
                                                    onSelect={setExportFrom}
                                                    locale={de}
                                                    disabled={(date) => exportTo ? date > exportTo : false}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className='space-y-2'>
                                        <label className='text-xs text-muted-foreground'>Bis</label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant='outline' className='w-[160px] justify-start text-left font-normal'>
                                                    <CalendarIcon className='h-4 w-4 mr-2' />
                                                    {exportTo
                                                        ? format(exportTo, 'dd.MM.yyyy', { locale: de })
                                                        : 'Enddatum'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className='w-auto p-0' align='start'>
                                                <Calendar
                                                    mode='single'
                                                    selected={exportTo}
                                                    onSelect={setExportTo}
                                                    locale={de}
                                                    disabled={(date) => exportFrom ? date < exportFrom : false}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </div>
                                <Button
                                    className='w-full'
                                    onClick={handleExport}
                                    disabled={!exportFrom || !exportTo || isExporting}
                                >
                                    {isExporting ? (
                                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                    ) : (
                                        <Download className='h-4 w-4 mr-2' />
                                    )}
                                    {isExporting ? 'Exportiere...' : 'CSV herunterladen'}
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>

                {/* Filters */}
                <Card className='mb-6'>
                    <CardContent className='pt-6'>
                        <div className='flex flex-wrap gap-4'>
                            <div className='flex-1 min-w-[200px]'>
                                <div className='relative'>
                                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Suche nach Kunde, Rechnungs- oder Entwurfsnummer...'
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setPage(0);
                                        }}
                                        className='pl-10'
                                    />
                                </div>
                            </div>
                            <Select
                                value={selectedStoreId}
                                onValueChange={(value) => {
                                    setSelectedStoreId(value);
                                    setPage(0);
                                }}
                            >
                                <SelectTrigger className='w-[200px]'>
                                    <SelectValue placeholder='Alle Filialen' />
                                </SelectTrigger>
                                <SelectContent>
                                    {isAdmin && <SelectItem value='all'>Alle Filialen</SelectItem>}
                                    {stores.map((store) => (
                                        <SelectItem key={store.id} value={String(store.id)}>
                                            {store.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={selectedType}
                                onValueChange={(value) => {
                                    setSelectedType(value);
                                    setPage(0);
                                }}
                            >
                                <SelectTrigger className='w-[220px]'>
                                    <SelectValue placeholder='Alle Typen' />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='all'>Alle Typen</SelectItem>
                                    <SelectItem value='GOA_INVOICE'>GOÄ</SelectItem>
                                    <SelectItem value='GOA_REFUND_INVOICE'>GOÄ Gutschrift</SelectItem>
                                    <SelectItem value='BUNDLE_INVOICE'>Bundle</SelectItem>
                                    <SelectItem value='BUNDLE_REFUND_INVOICE'>Bundle Gutschrift</SelectItem>
                                    <SelectItem value='GENERAL_INVOICE'>Allgemein</SelectItem>
                                    <SelectItem value='GENERAL_REFUND_INVOICE'>Allgemein Gutschrift</SelectItem>
                                    <SelectItem value='GIFT_CARD_INVOICE'>Gutschein</SelectItem>
                                    <SelectItem value='GIFT_CARD_REFUND_INVOICE'>Gutschein Gutschrift</SelectItem>
                                    <SelectItem value='AUSFALL_INVOICE'>Ausfallentschädigung</SelectItem>
                                    <SelectItem value='AUSFALL_REFUND_INVOICE'>Ausfallentschädigung Gutschrift</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select
                                value={selectedBillingDraftType}
                                onValueChange={(value) => {
                                    setSelectedBillingDraftType(value);
                                    setPage(0);
                                }}
                            >
                                <SelectTrigger className='w-[220px]'>
                                    <SelectValue placeholder='Alle Auftragsarten' />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='all'>Alle Auftragsarten</SelectItem>
                                    <SelectItem value='NORMAL'>Standard</SelectItem>
                                    <SelectItem value='INFLUENCER'>Influencer</SelectItem>
                                    <SelectItem value='MARKETING'>Marketing</SelectItem>
                                    <SelectItem value='ACADEMY'>Academy</SelectItem>
                                    <SelectItem value='KULANZ'>Kulanz/Beschwerde</SelectItem>
                                    <SelectItem value='CANCELLATION_FEE'>Ausfallentschädigung</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card>
                    <CardContent className='p-0'>
                        {isLoading ? (
                            <div className='p-6 space-y-4'>
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className='h-12 w-full' />
                                ))}
                            </div>
                        ) : (
                            <>
                                <Table>
                                    <TableHeader>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map((header) => (
                                                    <TableHead key={header.id}>
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableHeader>
                                    <TableBody>
                                        {table.getRowModel().rows?.length ? (
                                            table.getRowModel().rows.map((row) => (
                                                <TableRow
                                                    key={row.id}
                                                    className='cursor-pointer hover:bg-muted/50 relative'
                                                >
                                                    {row.getVisibleCells().map((cell, index) => (
                                                        <TableCell key={cell.id}>
                                                            {index === 0 && (
                                                                <Link
                                                                    to={`/invoice/${row.original.id}`}
                                                                    className="absolute inset-0 z-[1]"
                                                                    tabIndex={-1}
                                                                />
                                                            )}
                                                            {cell.column.id === 'download' || cell.column.id === 'customer' || cell.column.id === 'billingDraft' || cell.column.id === 'invoiceNumber' ? (
                                                                <div className="relative z-[2]">
                                                                    {flexRender(
                                                                        cell.column.columnDef.cell,
                                                                        cell.getContext()
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                flexRender(
                                                                    cell.column.columnDef.cell,
                                                                    cell.getContext()
                                                                )
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={columns.length}
                                                    className='h-24 text-center'
                                                >
                                                    Keine Rechnungen gefunden.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>

                                {/* Pagination */}
                                {invoicesData && invoicesData.pageCount > 1 && (
                                    <div className='flex items-center justify-between px-4 py-4 border-t'>
                                        <div className='text-sm text-muted-foreground'>
                                            Seite {page + 1} von {invoicesData.pageCount} ({invoicesData.totalCount} Rechnungen)
                                        </div>
                                        <div className='flex gap-2'>
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                onClick={() => setPage(page - 1)}
                                                disabled={page === 0}
                                            >
                                                Zurück
                                            </Button>
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                onClick={() => setPage(page + 1)}
                                                disabled={page >= invoicesData.pageCount - 1}
                                            >
                                                Weiter
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Content>
    );
};

export default Invoices;
